/**
 * Outreach & messaging kit (PE-2188 FE, backend PE-2187) — mirrors osprey-api's
 * `src/domain/outreach/dto/outreach.dto.ts` and the Prisma enums in `outreach.model.prisma`.
 * osprey-api OWNS this data (its own `outreach` tables); only the contact itself is proxied
 * from order-service's sales schema.
 */

export const OutreachEngagementModel = {
  WORKING_PREVIEW: 'WORKING_PREVIEW',
  FOURTEEN_DAY_PROMISE: 'FOURTEEN_DAY_PROMISE',
  AFRICAN_BRANDS_MARKETPLACE: 'AFRICAN_BRANDS_MARKETPLACE',
  OTHER: 'OTHER',
} as const;
export type OutreachEngagementModel =
  (typeof OutreachEngagementModel)[keyof typeof OutreachEngagementModel];

export const OutreachChannel = {
  WHATSAPP: 'WHATSAPP',
  INSTAGRAM_DM: 'INSTAGRAM_DM',
} as const;
export type OutreachChannel = (typeof OutreachChannel)[keyof typeof OutreachChannel];

export const OutreachMessageOrigin = {
  AI_DRAFTED: 'AI_DRAFTED',
  MANUAL: 'MANUAL',
} as const;
export type OutreachMessageOrigin =
  (typeof OutreachMessageOrigin)[keyof typeof OutreachMessageOrigin];

/** Same six values as the sales-contact stage — the follow-up cadence is configured per stage. */
export type OutreachContactStage =
  | 'PROSPECT'
  | 'IN_CONVERSATION'
  | 'QUOTED'
  | 'SIGNED'
  | 'ACTIVE_MERCHANT'
  | 'LOST';

export const ENGAGEMENT_MODEL_LABELS: Record<OutreachEngagementModel, string> = {
  WORKING_PREVIEW: 'Working preview',
  FOURTEEN_DAY_PROMISE: '14-day promise',
  AFRICAN_BRANDS_MARKETPLACE: 'African Brands Marketplace',
  OTHER: 'Other',
};

export const CHANNEL_LABELS: Record<OutreachChannel, string> = {
  WHATSAPP: 'WhatsApp',
  INSTAGRAM_DM: 'Instagram DM',
};

export const ORIGIN_LABELS: Record<OutreachMessageOrigin, string> = {
  AI_DRAFTED: 'AI-drafted',
  MANUAL: 'Manual',
};

export const ORIGIN_CLASSES: Record<OutreachMessageOrigin, string> = {
  AI_DRAFTED: 'bg-purple-100 text-purple-700',
  MANUAL: 'bg-muted text-muted-foreground',
};

/**
 * The stages a follow-up cadence can be set for. SIGNED / ACTIVE_MERCHANT / LOST are excluded
 * from the due queue server-side, so a cadence on them would never fire — not offered.
 */
export const CADENCE_STAGES: OutreachContactStage[] = ['PROSPECT', 'IN_CONVERSATION', 'QUOTED'];

export interface OutreachTemplate {
  id: string;
  engagementModel: OutreachEngagementModel;
  channel: OutreachChannel;
  name: string;
  body: string;
  isActive: boolean;
  originAiDrafted: boolean;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachTemplateQuery {
  engagementModel?: OutreachEngagementModel;
  channel?: OutreachChannel;
  isActive?: boolean;
}

export interface CreateOutreachTemplatePayload {
  engagementModel: OutreachEngagementModel;
  channel: OutreachChannel;
  name: string;
  body: string;
  originAiDrafted?: boolean;
}

/** engagementModel/channel are fixed at create — UpdateOutreachTemplateDto omits them. */
export interface UpdateOutreachTemplatePayload {
  name?: string;
  body?: string;
  isActive?: boolean;
}

export interface OutreachBrandCard {
  id: string;
  contactId: string;
  headline: string | null;
  summary: string | null;
  keySellingPoints: string[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface OutreachDraft {
  contactId: string;
  engagementModel: OutreachEngagementModel;
  draftBody: string;
  generatedAt: string;
}

export interface OutreachSend {
  id: string;
  contactId: string;
  channel: OutreachChannel;
  sendingAccount: string;
  templateId: string | null;
  engagementModel: OutreachEngagementModel | null;
  messageBody: string;
  origin: OutreachMessageOrigin;
  sentById: string | null;
  sentAt: string;
}

export interface CreateOutreachSendPayload {
  channel: OutreachChannel;
  sendingAccount: string;
  templateId?: string;
  engagementModel?: OutreachEngagementModel;
  messageBody: string;
  origin: OutreachMessageOrigin;
}

export interface FollowUpStageCadence {
  stage: OutreachContactStage;
  intervalDays: number;
  updatedById: string | null;
  updatedAt: string;
}

export interface FollowUpContactOverride {
  contactId: string;
  intervalDays: number;
  updatedById: string | null;
  updatedAt: string;
}

export interface DueContact {
  contactId: string;
  name: string;
  businessName: string | null;
  stage: OutreachContactStage;
  lastSentAt: string | null;
  daysSinceLastSend: number | null;
  effectiveCadenceDays: number;
}
